'use client';

import { useLayoutEffect, type RefObject } from 'react';
import { usePathname } from 'next/navigation';
import { usePageChrome } from '@components/layout/page-chrome-context';
import { clampScroll } from './dock-scroll-state';

// Shared across tab switches, but intentionally not persisted across reloads.
const scrollPositions = new Map<string, number>();

/** One scroll subscription owns restoration and title progress.
 * Transient visual values go straight to CSS, never through page React state.
 */
export function useMobileChromeScroll(ref: RefObject<HTMLDivElement | null>) {
  const pathname = usePathname();
  const { chrome } = usePageChrome();

  useLayoutEffect(() => {
    const scroller = ref.current;
    const shell = scroller?.closest<HTMLElement>('[data-app-shell]');
    if (!scroller || !shell) return;
    const title = scroller.querySelector<HTMLElement>(
      '[data-mobile-title-text]'
    );
    let frame = 0;
    let readyFrame = 0;
    let restored = false;
    let titleStart = 0;
    let titleHeight = 0;
    let lastOffset = 0;
    const offset = () =>
      clampScroll(
        scroller.scrollTop,
        scroller.scrollHeight - scroller.clientHeight
      );

    const paint = () => {
      frame = 0;
      if (!restored) return;
      const y = offset();
      lastOffset = y;
      scrollPositions.set(pathname, y);
      const progress =
        titleHeight > 0
          ? Math.max(0, Math.min(1, (y - titleStart) / titleHeight))
          : 0;
      shell.style.setProperty('--mobile-title-progress', String(progress));
      shell.style.setProperty(
        '--mobile-title-visibility',
        progress > 0 ? 'visible' : 'hidden'
      );
      shell.style.setProperty(
        '--mobile-divider-opacity',
        String(Math.min(1, y / 16))
      );
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(paint);
    };
    const measure = () => {
      if (title) {
        const bounds = title.getBoundingClientRect();
        titleStart =
          bounds.top -
          scroller.getBoundingClientRect().top +
          scroller.scrollTop;
        titleHeight = bounds.height;
      }
      schedule();
    };

    shell.dataset.mobileScrollRestoring = '';
    // Content and page-chrome registration must paint before restoring a tab.
    const restoreFrame = requestAnimationFrame(() => {
      scroller.scrollTop = scrollPositions.get(pathname) ?? 0;
      lastOffset = offset();
      measure();
      restored = true;
      readyFrame = requestAnimationFrame(() => {
        delete shell.dataset.mobileScrollRestoring;
      });
    });
    scroller.addEventListener('scroll', schedule, { passive: true });
    const observer = new ResizeObserver(measure);
    observer.observe(scroller);
    if (title) observer.observe(title);

    return () => {
      if (restored) scrollPositions.set(pathname, lastOffset);
      cancelAnimationFrame(restoreFrame);
      cancelAnimationFrame(frame);
      cancelAnimationFrame(readyFrame);
      observer.disconnect();
      scroller.removeEventListener('scroll', schedule);
      delete shell.dataset.mobileScrollRestoring;
      shell.style.removeProperty('--mobile-title-progress');
      shell.style.removeProperty('--mobile-title-visibility');
      shell.style.removeProperty('--mobile-divider-opacity');
    };
  }, [pathname, ref, chrome?.title, chrome?.mobileVariant]);
}
